import { isPrintGatewayStatus, type PrintGatewayStatus } from "./gatewayStatus";
import { t, translateMessage } from "./i18n";
import type {
  Orders,
  PrintJob,
  SalesPeriodReport,
  Session,
  SetupPatch,
  Setup,
  SyncMeta,
  User
} from "./types";
import { diagnostics } from "./diagnostics";
import { APP_VERSION, APP_VERSION_CODE } from "./utils";

declare const __BUILD_ID__: string;

export class ApiError extends Error {
  status: number;
  code: string;
  payload: Record<string, unknown>;
  requestId: string;
  actionId?: string;

  constructor(
    status: number,
    payload: Record<string, unknown>,
    requestId = "",
    actionId?: string
  ) {
    const message =
      (typeof payload.message === "string" && payload.message) ||
      (typeof payload.error === "string" && payload.error) ||
      `HTTP ${status}`;
    super(translateMessage(message));
    this.name = "ApiError";
    this.status = status;
    this.code = typeof payload.error === "string" ? payload.error : "";
    this.payload = payload;
    this.requestId = requestId;
    this.actionId = actionId;
  }
}

export type CompletedLoginResponse = {
  ok: boolean;
  auth_state: "complete";
  user_id: number;
  username: string;
  device_id: string;
  device_name: string;
  session_token: string;
  login_at: number;
  replaced_existing_session: boolean;
  previous_device_name: string;
  diagnostic_session_id?: string;
  user: User;
  business?: {
    id?: string;
    slug: string;
    name?: string;
    status?: string;
    access_until?: number;
  };
  recovery_codes?: string[];
};

export type MfaChallengeResponse = {
  ok: boolean;
  auth_state: "mfa_required" | "mfa_enrollment_required";
  business?: CompletedLoginResponse["business"];
  user_id: number;
  challenge_token: string;
  expires_at: number;
  manual_key?: string;
  otpauth_uri?: string;
  qr_png?: string;
};

export type LoginResponse = CompletedLoginResponse | MfaChallengeResponse;

type CreateOrderResponse = {
  ok: boolean;
  deduplicated: boolean;
  order_id: number;
  event_id: number;
  order_number: number;
  orders?: Orders;
  order_delta?: {
    orders: Orders["orders"];
    order_events: Orders["order_events"];
    order_items: Orders["order_items"];
  };
};

export type PrintResponse = {
  ok: boolean;
  deduplicated: boolean;
  client_delivery_required: boolean;
  job: PrintJob;
};

type HealthResponse = {
  ok: boolean;
  server_time: number;
  schema_version: number;
  api_version: number;
  deployment_mode: string;
};

type SetupPatchResponse = {
  ok: boolean;
  counts: Record<string, number>;
  setup: Setup;
};

export type TableLayoutResponse = {
  ok: boolean;
  setup_revision: string;
  revision: string;
  tables: Array<{
    id: number;
    grid_row: number;
    grid_col: number;
    page_id: number;
  }>;
};

const SESSION_FAILURES = new Set([
  "session_replaced",
  "no_active_session",
  "invalid_session",
  "access_changed",
  "subscription_inactive",
  "subscription_expired",
  "business_disabled",
  "user_not_active",
  "mfa_setup_required"
]);

export class EasyOrdersApi {
  private session: Session | null;
  onSessionInvalid?: (error: ApiError) => void;

  constructor(session: Session | null = null) {
    this.session = session;
    this.configureDiagnostics(session);
  }

  setSession(session: Session | null): void {
    this.session = session;
    this.configureDiagnostics(session);
  }

  private configureDiagnostics(session: Session | null): void {
    diagnostics.configureAuth(session ? {
      businessSlug: session.businessSlug,
      userId: session.user.id,
      deviceId: session.deviceId,
      deviceName: session.deviceName,
      token: session.token,
      diagnosticSessionId: session.diagnosticSessionId
    } : null);
  }

  async login(
    businessSlug: string,
    username: string,
    password: string,
    deviceId: string,
    deviceName: string,
    actionId?: string
  ): Promise<LoginResponse> {
    return this.request<LoginResponse>("POST", "/api/session/login", {
      ...(businessSlug ? { business_slug: businessSlug } : {}),
      username,
      password,
      device_id: deviceId,
      device_name: deviceName
    }, businessSlug, { actionId });
  }

  async completeMfa(
    businessSlug: string,
    challengeToken: string,
    code: string,
    actionId?: string
  ): Promise<CompletedLoginResponse> {
    return this.request<CompletedLoginResponse>("POST", "/api/session/mfa/complete", {
      ...(businessSlug ? { business_slug: businessSlug } : {}),
      challenge_token: challengeToken,
      code
    }, businessSlug, { actionId });
  }

  async logout(actionId?: string): Promise<void> {
    if (!this.session) {
      return;
    }
    await this.request("POST", "/api/session/logout", {
      user_id: this.session.user.id,
      device_id: this.session.deviceId,
      session_token: this.session.token
    }, undefined, { actionId });
  }

  health(actionId?: string): Promise<HealthResponse> {
    return this.request("GET", "/api/health", undefined, undefined, { actionId, poll: true });
  }

  meta(actionId?: string): Promise<SyncMeta> {
    return this.request("GET", "/api/sync/meta", undefined, undefined, { actionId, poll: true });
  }

  setup(actionId?: string): Promise<Setup> {
    return this.request("GET", "/api/setup", undefined, undefined, { actionId });
  }

  orders(actionId?: string): Promise<Orders> {
    return this.request("GET", "/api/orders", undefined, undefined, { actionId });
  }

  /** Admin-only: replaces the shared setup (menu, tables, users, settings) for the business. */
  saveSetup(setup: Setup, actionId?: string): Promise<{ ok: boolean; counts?: Record<string, number> }> {
    return this.request("POST", "/api/setup", setup as unknown as Record<string, unknown>, undefined, { actionId });
  }

  patchSetup(patch: SetupPatch, actionId?: string): Promise<SetupPatchResponse> {
    return this.request(
      "POST",
      "/api/setup/patch",
      patch as unknown as Record<string, unknown>,
      undefined,
      { actionId }
    );
  }

  async printGatewayStatus(): Promise<PrintGatewayStatus | null> {
    try {
      const result = await this.request<unknown>("GET", "/api/print/gateway");
      return isPrintGatewayStatus(result) ? result : null;
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  }

  changePassword(newPassword: string, actionId?: string): Promise<{ ok: boolean }> {
    return this.request("POST", "/api/account/password", {
      new_password: newPassword
    }, undefined, { actionId });
  }

  disableMfa(actionId?: string): Promise<{ ok: boolean; session_revoked: boolean }> {
    return this.request("POST", "/api/account/mfa/disable", {}, undefined, { actionId });
  }

  beginMfaSetup(actionId?: string): Promise<MfaChallengeResponse> {
    return this.request("POST", "/api/account/mfa/setup", {}, undefined, { actionId });
  }

  /* Your own interface preferences. The server takes the user id from the session, so there is
     no account parameter to get wrong. */
  getPreferences(): Promise<{ preferences: Record<string, unknown>; updated_at: number }> {
    return this.request("GET", "/api/account/preferences", undefined, undefined, { poll: true });
  }

  savePreferences(
    preferences: Record<string, unknown>
  ): Promise<{ ok: boolean; preferences: Record<string, unknown> }> {
    return this.request("POST", "/api/account/preferences", { preferences });
  }

  moveTableLayout(
    tableId: number,
    targetRow: number,
    targetCol: number,
    actionId?: string
  ): Promise<TableLayoutResponse> {
    return this.request("POST", "/api/tables/layout/move", {
      table_id: tableId,
      target_row: targetRow,
      target_col: targetCol
    }, undefined, { actionId });
  }

  /* Ad-hoc tables go through their own endpoint rather than the setup patch, because setup
     writes are admin-only and a waiter must be able to open an order on a new table. */
  createTemporaryTable(
    label: string,
    pageId: number,
    actionId?: string
  ): Promise<{ ok: boolean; created: boolean; table_id: number }> {
    return this.request("POST", "/api/tables/temporary", {
      label,
      page_id: pageId
    }, undefined, { actionId });
  }

  clearOrders(actionId?: string): Promise<{ ok: boolean; orders: Orders }> {
    return this.request("POST", "/api/orders/clear", {}, undefined, { actionId });
  }

  currentSalesPeriodReport(actionId?: string): Promise<SalesPeriodReport> {
    return this.request("GET", "/api/reports/current-period", undefined, undefined, { actionId });
  }

  createOrder(payload: Record<string, unknown>, actionId?: string): Promise<CreateOrderResponse> {
    return this.request("POST", "/api/orders/create", payload, undefined, { actionId });
  }

  closeTable(tableLabel: string, actionId?: string): Promise<{ ok: boolean; changed: number; orders: Orders }> {
    return this.request("POST", "/api/orders/close-table", {
      table_label: tableLabel
    }, undefined, { actionId });
  }

  setServed(lineIds: number[], served: boolean, actionId?: string): Promise<{ ok: boolean; changed: number; orders: Orders }> {
    return this.request("POST", "/api/orders/served", {
      line_ids: lineIds,
      served
    }, undefined, { actionId });
  }

  cancelOrder(
    orderId: number,
    waiter: string,
    submissionId: string,
    note = "",
    actionId?: string
  ): Promise<{ ok: boolean; event_id: number; orders: Orders }> {
    return this.request("POST", "/api/orders/cancel", {
      order_id: orderId,
      waiter,
      note,
      submission_id: submissionId
    }, undefined, { actionId });
  }

  correctProduct(input: {
    orderId: number;
    lineId: number;
    targetQuantity: number;
    withoutOptions: string;
    extras: string;
    counters: string;
    itemComment: string;
    takeaway: boolean;
    price: number;
    waiter: string;
    submissionId: string;
  }, actionId?: string): Promise<{ ok: boolean; event_id: number; orders: Orders }> {
    return this.request("POST", "/api/orders/cancel-product", {
      order_id: input.orderId,
      line_id: input.lineId,
      target_quantity: input.targetQuantity,
      without_options: input.withoutOptions,
      extras: input.extras,
      counters: input.counters,
      item_comment: input.itemComment,
      takeaway: input.takeaway,
      price: input.price,
      waiter: input.waiter,
      submission_id: input.submissionId,
      note: ""
    }, undefined, { actionId });
  }

  changeOrderTable(input: {
    orderId: number;
    sourceTableLabel: string;
    targetTableLabel: string;
    waiter: string;
    submissionId: string;
  }, actionId?: string): Promise<{ ok: boolean; event_id: number; orders: Orders }> {
    return this.request("POST", "/api/orders/change-table", {
      order_id: input.orderId,
      source_table_label: input.sourceTableLabel,
      target_table_label: input.targetTableLabel,
      waiter: input.waiter,
      submission_id: input.submissionId
    }, undefined, { actionId });
  }

  mergeTables(label: string, tableIds: number[], actionId?: string): Promise<{ ok: boolean; orders?: Orders }> {
    return this.request("POST", "/api/tables/merge", {
      label,
      table_ids: tableIds
    }, undefined, { actionId });
  }

  /* Adds tables to an existing merge in one server transaction. The label is the merge's own;
     the server keeps it and ignores this copy. */
  extendMerge(mergeId: number, label: string, tableIds: number[], actionId?: string): Promise<{ ok: boolean; orders?: Orders }> {
    return this.request("POST", "/api/tables/merge", {
      merge_id: mergeId,
      label,
      table_ids: tableIds
    }, undefined, { actionId });
  }

  splitTable(mergeId: number, actionId?: string): Promise<{ ok: boolean; orders?: Orders }> {
    return this.request("POST", "/api/tables/split", {
      merge_id: mergeId
    }, undefined, { actionId });
  }

  queuePrint(input: {
    printJobId: string;
    submissionId?: string;
    orderId: number;
    eventId: number;
    profile: "order" | "history";
    printerIds: number[];
    routeMode?: "all" | "automatic" | "manual";
    routing?: Record<string, number[]>;
  }, actionId?: string): Promise<PrintResponse> {
    return this.request("POST", "/api/print/order", {
      print_job_id: input.printJobId,
      submission_id: input.submissionId ?? "",
      order_id: input.orderId,
      event_id: input.eventId,
      profile: input.profile,
      printer_ids: input.printerIds,
      route_mode: input.routeMode ?? "all",
      routing: input.routing ?? {}
    }, undefined, { actionId });
  }

  /**
   * Sends ticket bytes the PWA rendered itself, the same call the Android app makes.
   *
   * The server prints these over the tunnel. order_id/event_id are still included so that if the
   * server cannot reach the printers, an on-site relay can claim the job and render it locally,
   * exactly as it does today.
   */
  rawPrint(input: {
    printJobId: string;
    submissionId?: string;
    orderId: number;
    eventId: number;
    clientProfile?: "probe" | "test";
    routeMode?: "all" | "automatic" | "manual";
    routing?: Record<string, number[]>;
    printerPayloads: Array<{
      printer_id: number;
      payload_base64?: string;
      payload_gzip_base64?: string;
    }>;
  }, actionId?: string): Promise<PrintResponse> {
    return this.request("POST", "/api/print/raw", {
      print_job_id: input.printJobId,
      submission_id: input.submissionId ?? "",
      order_id: input.orderId,
      event_id: input.eventId,
      client_profile: input.clientProfile ?? "",
      printer_payloads: input.printerPayloads,
      route_mode: input.routeMode ?? "all",
      routing: input.routing ?? {}
    }, undefined, { actionId });
  }

  testPrint(printerId: number, actionId?: string): Promise<PrintResponse> {
    return this.request("POST", "/api/print/test", {
      print_job_id: `web-test-${crypto.randomUUID()}`,
      printer_id: printerId
    }, undefined, { actionId });
  }

  probePrinter(printerId: number, actionId?: string): Promise<PrintResponse> {
    return this.request("POST", "/api/print/probe", {
      print_job_id: `web-probe-${crypto.randomUUID()}`,
      printer_id: printerId
    }, undefined, { actionId });
  }

  printStatus(printJobId: string, actionId?: string): Promise<{ ok: boolean; found: boolean; job?: PrintJob }> {
    return this.request("POST", "/api/print/status", {
      print_job_id: printJobId
    }, undefined, { actionId, poll: true });
  }

  private async request<T>(
    method: "GET" | "POST",
    path: string,
    body?: Record<string, unknown>,
    businessOverride?: string,
    options: { actionId?: string; poll?: boolean } = {}
  ): Promise<T> {
    const requestId = crypto.randomUUID();
    const startedAt = performance.now();
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), method === "GET" ? 12_000 : 25_000);
    const headers: Record<string, string> = {
      Accept: "application/json",
      "X-EasyOrders-App-Version": `pwa-${APP_VERSION}`,
      "X-EasyOrders-App-Version-Code": String(APP_VERSION_CODE),
      "X-EasyOrders-Build-Id": __BUILD_ID__,
      "X-EasyOrders-Request-Id": requestId,
      "X-EasyOrders-Diagnostic-Session-Id": diagnostics.getSessionId(),
      "X-EasyOrders-Platform": "pwa"
    };
    if (options.actionId) {
      headers["X-EasyOrders-Action-Id"] = options.actionId;
    }
    if (method === "POST") {
      headers["Content-Type"] = "application/json";
    }
    const business = businessOverride || this.session?.businessSlug;
    if (business) {
      headers["X-EasyOrders-Business"] = business;
    }
    if (this.session) {
      headers["X-EasyOrders-User-Id"] = String(this.session.user.id);
      headers["X-EasyOrders-Device-Id"] = this.session.deviceId;
      headers["X-EasyOrders-Device-Name"] = this.session.deviceName;
      headers["X-EasyOrders-Session-Token"] = this.session.token;
    }
    try {
      const response = await fetch(path, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        cache: "no-store",
        credentials: "same-origin",
        signal: controller.signal
      });
      const payload = (await response.json().catch(() => ({
        error: "invalid_server_response",
        message: t("Ο server επέστρεψε μη έγκυρη απάντηση.")
      }))) as Record<string, unknown>;
      if (!response.ok) {
        const error = new ApiError(response.status, payload, requestId, options.actionId);
        if (
          this.session &&
          (SESSION_FAILURES.has(error.code) ||
            (typeof payload.reason === "string" && SESSION_FAILURES.has(payload.reason)))
        ) {
          this.onSessionInvalid?.(error);
        }
        throw error;
      }
      const duration = Math.round(performance.now() - startedAt);
      /* Every poll is issued with an actionId, so the old `Boolean(options.actionId)` arm was
         always true and this suppression never suppressed anything — background polling wrote
         more rows than every real action combined. A successful poll is only worth recording
         when it was slow enough to be a symptom. Failures take the catch branch regardless. */
      if (!options.poll || duration >= 4_000) {
        diagnostics.record({
          name: "network.request",
          category: "NETWORK",
          severity: duration >= 4_000 ? "WARNING" : "INFO",
          outcome: "succeeded",
          actionId: options.actionId,
          requestId,
          metadata: {
            route: path,
            method,
            status: response.status,
            duration_ms: duration,
            poll: options.poll ?? false
          }
        });
      }
      return payload as T;
    } catch (error) {
      const duration = Math.round(performance.now() - startedAt);
      const timeout = error instanceof DOMException && error.name === "AbortError";
      const status = error instanceof ApiError ? error.status : 0;
      const errorCode = error instanceof ApiError && error.code
        ? error.code
        : timeout
          ? "request_timeout"
          : navigator.onLine
            ? "network_unreachable"
            : "offline";
      diagnostics.record({
        name: "network.request",
        category: "NETWORK",
        severity: status >= 500 || status === 0 ? "ERROR" : "WARNING",
        outcome: navigator.onLine ? "failed" : "offline",
        actionId: options.actionId,
        requestId,
        metadata: {
          route: path,
          method,
          status,
          duration_ms: duration,
          error_code: errorCode,
          error_kind: timeout ? "TimeoutError" : error instanceof ApiError ? "ApiError" : "NetworkError",
          poll: options.poll ?? false,
          online: navigator.onLine
        },
        coalesceKey: options.poll ? `poll:${path}:${errorCode}` : undefined,
        coalesceMs: options.poll ? 15_000 : undefined
      });
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof DOMException && error.name === "AbortError") {
        throw Object.assign(new Error(t("Ο server άργησε να απαντήσει.")), { code: "request_timeout" });
      }
      throw Object.assign(new Error(navigator.onLine
        ? t("Δεν ήταν δυνατή η σύνδεση με τον server.")
        : t("Δεν υπάρχει σύνδεση στο internet.")), {
        code: navigator.onLine ? "network_unreachable" : "offline"
      });
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
